import type { ColumnsType } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";

import { translate } from "app/filters/i18nFilter";
import { LocationCell } from "app/pages/sales-intelligence/common-components/table-cells";
import SocialLinkCell from "app/pages/sales-intelligence/common-components/table-cells/components/SocialLinkCell/SocialLinkCell";
import { formatNumberWithLimit } from "app/pages/sales-intelligence/utils/numbers";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import ContactAccuracyGradeCell from "../../components/cells/ContactAccuracyGradeCell/ContactAccuracyGradeCell";
import ContactCompanyNameCell from "../../components/cells/ContactCompanyNameCell/ContactCompanyNameCell";
import ContactDepartmentCell from "../../components/cells/ContactDepartmentCell/ContactDepartmentCell";
import ContactLastUpdateCell from "../../components/cells/ContactLastUpdateCell/ContactLastUpdateCell";
import ContactNameCell from "../../components/cells/ContactNameCell/ContactNameCell";
import type { IEngageActionsCellBaseProps } from "../../components/cells/EngageActionsCell/EngageActionsCell";
import EngageActionsCell from "../../components/cells/EngageActionsCell/EngageActionsCell";
import { StyledTruncatedTextCell } from "../../components/cells/styles";
import ContactDetailsCellContainer from "../../containers/ContactDetailsCellContainer/ContactDetailsCellContainer";
import type { ContactSearchEntry } from "../../types/common";

import { createRegularContactColumn } from "./utils";

export type ContactsTableColumnsExternalProps = IEngageActionsCellBaseProps & {
    totalCount?: number;
    onRefetchData?: () => Promise<unknown> | undefined;
};

export const createAllColumnsConfigFactory = ({
    onExportItemToExel,
    onExportItemToCRM,
    totalCount,
    onRefetchData,
}: ContactsTableColumnsExternalProps): Partial<Record<keyof ContactSearchEntry, ColumnsType<ContactSearchEntry>>> => {
    return {
        name: [
            createRegularContactColumn("name", {
                title: translate(
                    is.undefined(totalCount)
                        ? `si.contacts_search.table.column.name.title`
                        : `si.contacts_search.table.column.name.title-with-count`,
                    {
                        totalCount: formatNumberWithLimit(totalCount),
                    },
                ),
                fixed: true,
                width: 198,
                render: (value: string, rowData: ContactSearchEntry) => (
                    <ContactNameCell
                        value={value}
                        rowData={rowData}
                        withLink
                    />
                ),
            }),
        ],
        companyName: [
            createRegularContactColumn("companyName", {
                width: 195,
                render: (_, rowData: ContactSearchEntry) => <ContactCompanyNameCell rowData={rowData} />,
            }),
        ],
        privateData: [
            createRegularContactColumn("privateData", {
                title: translate("si.contacts_search.table.column.contact-details.title"),
                width: 244,
                render: (_, rowData: ContactSearchEntry) => (
                    <ContactDetailsCellContainer
                        rowData={rowData}
                        onApprove={onRefetchData}
                    />
                ),
            }),
        ],
        hasDirectPhoneNumbers: [
            createRegularContactColumn("hasDirectPhoneNumbers", {
                title: translate("si.contacts_search.table.column.actions.title"),
                width: 106,
                className: "column-overflow-visible",
                render: (_: unknown, rowData: ContactSearchEntry, index: number) => (
                    <EngageActionsCell
                        actionButtonDataAutomation={`contact-button.more-actions.index-${index}`}
                        rowData={rowData}
                        onExportItemToCRM={onExportItemToCRM}
                        onExportItemToExel={onExportItemToExel}
                    />
                ),
            }),
        ],
        title: [
            createRegularContactColumn("title", {
                width: 198,
                render: (value: string) => <StyledTruncatedTextCell>{String(value)}</StyledTruncatedTextCell>,
            }),
        ],
        department: [
            createRegularContactColumn("department", {
                width: 200,
                sorter: true,
                render: (value: string) => <ContactDepartmentCell value={value} />,
            }),
        ],
        numericCountryCode: [
            createRegularContactColumn("numericCountryCode", {
                width: 177,
                render: (value: number) => <LocationCell locationCode={value} />,
            }),
        ],
        accuracyGrade: [
            createRegularContactColumn("accuracyGrade", {
                tooltipTitle: translate(`si.contacts_search.table.column.accuracyGrade.tooltip`),
                width: 105,
                sorter: true,
                render: (value: string) => <ContactAccuracyGradeCell value={value} />,
            }),
        ],
        isRevealed: [
            createRegularContactColumn("isRevealed", {
                title: translate("si.contacts_search.table.column.last-update.title"),
                tooltipTitle: translate(`si.contacts_search.table.column.last-update.tooltip`),
                width: 150,
                render: (_, rowData: ContactSearchEntry) => <ContactLastUpdateCell rowData={rowData} />,
            }),
        ],
        linkedin: [
            createRegularContactColumn("linkedin", {
                width: 56,
                render: (_, rowData: ContactSearchEntry) => (
                    <SocialLinkCell
                        url={rowData.linkedin}
                        iconName="social-linkedin"
                        iconSize="xs"
                        tooltipText={translate("si.contacts_search.contacts.linked_in.tooltip")}
                        iconColor={colorsPalettes.carbon[300]}
                        isCentered
                    />
                ),
            }),
        ],
    };
};

export const createContactsResultsTableColumns = ({
    onExportItemToExel,
    onExportItemToCRM,
    totalCount,
    onRefetchData,
}: ContactsTableColumnsExternalProps): ColumnsType<ContactSearchEntry> => {
    const allColumnsConfig = createAllColumnsConfigFactory({
        onExportItemToExel,
        onExportItemToCRM,
        totalCount,
        onRefetchData,
    });

    return [
        ...(allColumnsConfig.name || []),
        ...(allColumnsConfig.companyName || []),
        ...(allColumnsConfig.privateData || []),
        ...(allColumnsConfig.hasDirectPhoneNumbers || []),
        ...(allColumnsConfig.title || []),
        ...(allColumnsConfig.department || []),
        ...(allColumnsConfig.numericCountryCode || []),
        ...(allColumnsConfig.accuracyGrade || []),
        ...(allColumnsConfig.isRevealed || []),
    ];
};
